/**
 * Sticky add-to-cart bar.
 *
 * Shows the sticky bar once the user scrolls past any element marked with
 * [data-sticky-atc-trigger] (typically the product-page Add to Cart button).
 * Hides again when the user scrolls back up above that element.
 *
 * Clicking the sticky CTA scrolls back to the closest passed trigger so the
 * user can interact with the real ATC (which Shopify wires up properly).
 *
 * If no trigger exists on the page, the bar never appears.
 */
(() => {
  const sticky = document.querySelector('[data-sticky-atc]');
  if (!sticky) return;

  const triggers = document.querySelectorAll('[data-sticky-atc-trigger]');
  if (!triggers.length) return;

  if (!('IntersectionObserver' in window)) return;

  // Track per-trigger pass state — bar is shown when any trigger has been scrolled past
  const passed = new WeakSet();

  const update = () => {
    let anyPassed = false;
    triggers.forEach((el) => {
      if (passed.has(el)) anyPassed = true;
    });
    sticky.classList.toggle('is-visible', anyPassed);
    sticky.setAttribute('aria-hidden', anyPassed ? 'false' : 'true');
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isAbove = entry.boundingClientRect.top < 0;
        if (!entry.isIntersecting && isAbove) {
          passed.add(entry.target);
        } else if (entry.isIntersecting || entry.boundingClientRect.top > 0) {
          passed.delete(entry.target);
        }
      });
      update();
    },
    { threshold: 0, rootMargin: '0px 0px 0px 0px' }
  );

  triggers.forEach((el) => observer.observe(el));

  // CTA click → scroll the trigger back into view rather than following href
  const cta = sticky.querySelector('.sticky-atc__cta');
  if (cta) {
    cta.addEventListener('click', (e) => {
      // Pick the topmost trigger that's been passed (the one above current scroll)
      let target = null;
      triggers.forEach((el) => {
        if (passed.has(el)) {
          if (!target || el.getBoundingClientRect().top > target.getBoundingClientRect().top) {
            target = el;
          }
        }
      });
      if (!target) target = triggers[0];
      if (!target) return;

      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Briefly focus the trigger for keyboard users (without scrolling again)
      if (typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    });
  }
})();
